import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, FileText, LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { APPLY_PATH, COMPANY } from "@/data/content";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

const STATUS_STYLES = {
  new: "border-aqua/50 bg-aqua/10 text-aqua",
  reviewing: "border-yellow-500/40 bg-yellow-400/10 text-yellow-700",
  contacted: "border-sky-500/40 bg-sky-400/10 text-sky-700",
  approved: "border-emerald-500/40 bg-emerald-400/10 text-emerald-700",
  declined: "border-red-500/40 bg-red-400/10 text-red-600",
};

const STATUS_COPY = {
  new: "Received — awaiting review",
  reviewing: "Under qualification review",
  contacted: "We've reached out — check your inbox",
  approved: "Approved — next steps underway",
  declined: "Not moving forward at this time",
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/account/applications`, { withCredentials: true })
      .then(({ data }) => setApps(data))
      .catch(() => setApps([]));
  }, []);

  const signOut = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div data-testid="account-page">
      <Navbar />
      <main className="min-h-screen bg-ink pb-24 pt-32">
        <div className="container-x max-w-3xl">
          <p className="eyebrow mb-2">Partner Account</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-offwhite" data-testid="account-title">
              Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            <button onClick={signOut} className="btn-ghost !px-4 !py-2 !text-xs" data-testid="account-signout-btn">
              <LogOut size={13} /> Sign out
            </button>
          </div>
          <p className="mt-2 text-sm text-muted">Signed in as {user?.email}</p>

          <h2 className="mt-10 font-display text-lg font-bold text-offwhite">Your Applications</h2>
          <div className="mt-5 space-y-4">
            {apps === null ? (
              <p className="py-12 text-center text-sm text-muted">Loading…</p>
            ) : apps.length === 0 ? (
              <div className="card-line rounded-xl p-10 text-center" data-testid="account-empty">
                <FileText size={22} className="mx-auto text-aqua" />
                <p className="mt-4 text-sm leading-relaxed text-muted">
                  No applications found for <span className="text-offwhite">{user?.email}</span>. If you applied with a
                  different email, sign in with that address instead.
                </p>
                <Link to={APPLY_PATH} className="btn-primary mt-6" data-testid="account-apply-btn">
                  Start Your Application <ArrowRight size={15} />
                </Link>
              </div>
            ) : (
              apps.map((a) => {
                const status = a.status || "new";
                return (
                  <div key={a.application_id} className="card-line rounded-xl p-6" data-testid={`account-app-${a.application_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-mono text-xs text-aqua">{a.application_id}</p>
                        <p className="mt-1 font-display text-lg font-bold text-offwhite">{a.legal_business_name}</p>
                        <p className="mt-0.5 text-xs text-muted">
                          {a.city}, {a.state} · Submitted {new Date(a.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </p>
                      </div>
                      <span className={`rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[status]}`}>
                        {status}
                      </span>
                    </div>
                    <p className="mt-4 border-t border-black/[0.07] pt-4 text-sm text-muted">{STATUS_COPY[status]}</p>
                  </div>
                );
              })
            )}
          </div>

          <p className="mt-8 text-xs leading-relaxed text-muted/70">
            Questions about your application? Contact {COMPANY.name} at{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-aqua underline underline-offset-2">{COMPANY.email}</a>{" "}
            or {COMPANY.phone}.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
